const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'

// Kodi runs on its own subdomain in deployed environments (kodi.staging.baltocloud.com,
// kodi.baltocloud.com). Local dev lets the Vite dev server proxy /ws/chat to the local
// Kodi container, so the same-origin URL is what we want there.
export const getKodiHost = (host = window.location.host) => {
  if (host.includes('localhost') || host.includes('baltocloud.local')) {
    return host
  }
  if (host === 'staging-web.baltocloud.com') {
    return 'kodi.staging.baltocloud.com'
  }
  return 'kodi.baltocloud.com'
}

export const COWORK_WS_URL = `${wsProtocol}//${getKodiHost()}/ws/chat`
export const KODI_SESSIONS_PAGE_SIZE = 50

// Slack for treating the chat as scrolled to the bottom. Float-rounding tolerance only
// (retina, browser zoom, fractional layout leave a sub-pixel remainder at the true
// bottom) — deliberately NOT a "close enough" window, so any real upward scroll counts.
export const AT_BOTTOM_EPSILON_PX = 2
// How far from the bottom before the jump-to-bottom button appears. Larger on purpose:
// the button shouldn't flicker in while the bottom is still on screen.
export const NEAR_BOTTOM_PX = 100

export const MAX_FEEDBACK_COMMENT_LENGTH = 1000

// Sent verbatim as the `categories` array, so the labels double as the persisted
// (plaintext, filterable) values. Used by the feedback control.
export const FEEDBACK_CATEGORIES = [
  'Inaccurate or made up',
  "Didn't follow my request",
  'Incomplete or missing detail',
  'Wrong data or filters',
  'Confusing or poorly formatted',
]

export const CONNECTION_STATES = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
}

export const CONNECTION_STATE_LABELS = {
  [CONNECTION_STATES.CONNECTING]: 'Connecting…',
  [CONNECTION_STATES.CONNECTED]: 'Connected',
  [CONNECTION_STATES.DISCONNECTED]: 'Disconnected',
}

export const WS_MESSAGE_TYPES = {
  AUTH: 'auth',
  AUTH_SUCCESS: 'auth_success',
  SYSTEM: 'system',
  PROMPT: 'prompt',
  // FE-observed signal with no user text (a Notetaker backfill reaching a
  // terminal state) that lets the BE start a turn on its own. Never echoed.
  EVENT: 'event',
  // Asks the BE to cancel the running turn. Closing the socket does NOT cancel
  // it — turns are detached from the connection so a refresh can't kill them —
  // so this is the only way to stop one. The ack is the turn's own
  // TURN_COMPLETE, which every tab tailing the session receives.
  STOP: 'stop',
  TEXT_DELTA: 'text_delta',
  TOOL_CALL_START: 'tool_call_start',
  TOOL_EXECUTING: 'tool_executing',
  TOOL_PROGRESS: 'tool_progress',
  TOOL_RESULT: 'tool_result',
  TURN_COMPLETE: 'turn_complete',
  // A turn the BE started on its own (a backfill nudge). There is no prompt to
  // echo, so this is the only sign the turn is running.
  TURN_STARTED: 'turn_started',
  REFRESH_TOOLS: 'refresh_tools',
  TOOLS_REFRESHED: 'tools_refreshed',
  ARTIFACT: 'artifact',
  ERROR: 'error',
  INFO: 'info',
  USER_MESSAGE: 'user_message',
  SESSION_RESTORE_START: 'session_restore_start',
  SESSION_RESTORE_END: 'session_restore_end',
  SESSION_RENAMED: 'session_renamed',
  // Boolean signal: the model proposed a change and is awaiting the user's
  // yes/no/other. The change summary is in the preceding text deltas. Approve/reject
  // go back as a prompt carrying an `approval` decision (see WS_APPROVAL_DECISIONS) so
  // the backend can enforce it; "suggest changes" goes back as an ordinary prompt.
  APPROVAL_REQUESTED: 'approval_requested',
  // Kodi asked a clarifying question. Carries { question, options, multi_select }
  // for the interactive window above the composer; the user's choice (or free
  // text) goes back as an ordinary prompt.
  INTERACTIVE_REQUEST: 'interactive_request',
  // The user's usage-policy standings; sent on connect and after each
  // completed turn. Feeds the header's usage hoverable.
  USAGE_UPDATE: 'usage_update',
}

// The user's explicit decision on a proposed change, sent as the `approval` field on
// the prompt. Must match the backend's expected values (chat_session._apply_approval_decision).
export const WS_APPROVAL_DECISIONS = {
  APPROVE: 'approve',
  DECLINE: 'decline',
}

export const MESSAGE_ROLES = {
  USER: 'user',
  KODI: 'kodi',
  ERROR: 'error',
  TOOL: 'tool',
  ARTIFACT: 'artifact',
}

export const TOOL_STATUSES = {
  CALLING: 'calling',
  EXECUTING: 'executing',
  COMPLETE: 'complete',
}

export const ARTIFACT_TOOLS = {
  RENDER_CHART: 'render_chart',
  RENDER_REPORT: 'render_report',
}

// Artifact types that should render inline (not trigger a download).
export const ARTIFACT_TYPE_CHART = 'chart'
export const ARTIFACT_TYPE_TABLE = 'table'
export const ARTIFACT_TYPE_BACKFILL = 'backfill_progress'
export const INLINE_ARTIFACT_TYPES = [
  ARTIFACT_TYPE_CHART,
  ARTIFACT_TYPE_TABLE,
  ARTIFACT_TYPE_BACKFILL,
]

export const UNTITLED_SESSION_LABEL = 'Untitled chat'

export const sessionDisplayName = (session) => session?.name || UNTITLED_SESSION_LABEL

export const TITLE_REVEAL_MS = 420

// Dog-themed loader phrases shown before a rationale lands, so Kodi never reads idle.
export const KODI_THINKING_PHRASES = [
  'Following the scent…',
  'Fetching the details…',
  'Digging in…',
  'On the trail…',
  'Nosing through the data…',
  'Tracking it down…',
  'Pawing through the records…',
  'Chasing it down…',
  'Rooting around…',
]

// Usage-policy display helpers shared by the header hoverable and the usage page.
export const formatPolicyWindow = (hours) => (hours % 24 === 0 ? `${hours / 24}d` : `${hours}h`)

export const policyUsagePercent = (policy) => {
  if (policy.token_limit === 0) {
    return 100
  }
  return Math.min(100, (policy.tokens_used / policy.token_limit) * 100)
}

// Named separately from the list below because `prompt` is a cross-repo contract, not
// just copy: Kodi arms its getting-started tour off a session's opening message, so
// this string must stay byte-identical to WALKTHROUGH_PROMPT in kodi's
// src/models/chat_session.py.
export const KODI_WALKTHROUGH = {
  key: 'walkthrough',
  label: 'A 3-minute tour of Kodi',
  prompt: 'Give me a quick tour of Kodi.',
}

// Pills offered on an empty chat, each prefilling its `prompt` into the composer. One
// for now; the row maps over this array, so a second pill is an entry here (plus an
// icon in SUGGESTION_ICONS if it wants one) and no other change.
export const COWORK_SUGGESTIONS = [KODI_WALKTHROUGH]

// Offered instead when the chat opens on a chart from Kodi Reports; the backend loads
// that chart into the first turn, so "this chart" resolves to it.
export const REPORT_CHART_SUGGESTIONS = [
  { key: 'explain-chart', label: 'Explain this chart', prompt: 'Explain what this chart shows.' },
  {
    key: 'chart-standouts',
    label: 'What stands out?',
    prompt: 'What stands out in this chart?',
  },
  {
    key: 'chart-drivers',
    label: "What's driving it?",
    prompt: "What's driving the pattern in this chart?",
  },
]
